import React, { useState, useEffect } from "react";
import ShieldIcon from "@mui/icons-material/Shield";
import "../styles/PrivacyStyles.css";

const sections = [
  {
    id: "01",
    title: "Information We Collect",
    content:
      "We collect information you provide directly when you register, place an order, request a quote, or contact us. This includes your full name, business name, email address, phone number, billing and shipping address, and GST/PAN details where applicable. We also automatically collect technical data such as IP address, browser type, device information, and pages visited to help us improve platform performance.",
  },
  {
    id: "02",
    title: "How We Use Your Information",
    content:
      "Your information is used to process and fulfill orders, issue GST-compliant invoices, provide after-sales and technical support, send order confirmations and shipping updates, and respond to your enquiries. We may also use aggregated, anonymized data to analyse platform usage trends and improve our service offerings. We do not use your personal data for profiling or automated decision-making.",
  },
  {
    id: "03",
    title: "Data Sharing & Third Parties",
    content:
      "We do not sell, rent, or trade your personal information to third parties. We may share your data with trusted logistics partners for order delivery, payment gateways for transaction processing, and government or regulatory authorities where required by law. All third-party partners are contractually obligated to handle your data in accordance with applicable privacy laws.",
  },
  {
    id: "04",
    title: "Business & Procurement Data",
    content:
      "For B2B customers, we may collect additional business information such as your company registration number, GSTIN, authorized contact persons, and procurement history. This data is used exclusively to manage your account, generate compliant tax documents, and facilitate bulk or credit-based orders. This information is treated with the same level of confidentiality as personal data.",
  },
  {
    id: "05",
    title: "Cookies & Tracking Technologies",
    content:
      "Our platform uses cookies and similar technologies to maintain session state, remember your preferences, and analyse traffic patterns. Essential cookies are required for the platform to function and cannot be disabled. Analytics cookies are used to understand how visitors interact with our site and can be opted out of via your browser settings. We do not use advertising or cross-site tracking cookies.",
  },
  {
    id: "06",
    title: "Data Retention",
    content:
      "We retain your personal data for as long as your account is active or as required to fulfil the purposes outlined in this policy. Transaction records, invoices, and order history may be retained for up to 7 years to comply with Indian tax and accounting regulations (GST Act, Income Tax Act). Upon account deletion, personal data not required for legal compliance will be permanently removed within 30 days.",
  },
  {
    id: "07",
    title: "Data Security",
    content:
      "We implement industry-standard security measures including SSL/TLS encryption for data in transit, secure server infrastructure, and access controls to prevent unauthorized access, alteration, or disclosure of your data. While we take every reasonable precaution, no system is entirely immune to security risks. We encourage you to use strong passwords and notify us immediately of any suspected unauthorized access to your account.",
  },
  {
    id: "08",
    title: "Your Rights",
    content:
      "You have the right to access, correct, or request deletion of your personal data held by Tecniqa. You may also request a copy of your data in a portable format or withdraw consent for specific data processing activities at any time. To exercise any of these rights, contact us at support@tecniqa.com. We will respond to all valid requests within 30 days in accordance with applicable law.",
  },
  {
    id: "09",
    title: "Children's Privacy",
    content:
      "The Tecniqa platform is intended for use by businesses and professionals. We do not knowingly collect personal information from individuals under the age of 18. If you believe a minor has provided us with their personal information, please contact us immediately and we will take prompt steps to remove the data.",
  },
  {
    id: "10",
    title: "Links to Third-Party Sites",
    content:
      "Our platform may contain links to third-party websites such as manufacturer portals, logistics tracking pages, or payment gateways. Tecniqa is not responsible for the privacy practices or content of these external sites. We recommend reviewing the privacy policy of any third-party site you visit.",
  },
  {
    id: "11",
    title: "Changes to This Policy",
    content:
      "We may update this Privacy Policy from time to time to reflect changes in our practices, technology, or legal requirements. The revised policy will be published on this page with an updated effective date. We encourage you to review this page periodically. Continued use of the platform after any update constitutes your acceptance of the revised policy.",
  },
  {
    id: "12",
    title: "Contact & Grievance Redressal",
    content:
      "For any privacy-related concerns, data requests, or grievances, please contact our Data Protection Officer at support@tecniqa.com or call +91 9668 246 683 during business hours (Mon–Sat, 9 AM – 6 PM). We are committed to resolving all privacy concerns promptly and transparently.",
  },
];

export default function PrivacyPolicy() {
  const [active, setActive] = useState(null);

  useEffect(() => {
    document.title = "Privacy Policy | Tecniqa";
  }, []);

  return (
    <div className="pp-root">

      {/* HERO */}
      <div className="pp-hero">
        <div className="pp-hero-grid" />
        <div className="pp-hero-orb pp-orb1" />
        <div className="pp-hero-orb pp-orb2" />

        <div className="pp-hero-content">



          <div className="pp-hero-badge">
            <div className="pp-badge-dot" />
            Privacy Policy
          </div>

          <h1 className="pp-hero-title">
            Your Data,<br />
            <span>Our Responsibility</span>
          </h1>

          <p className="pp-hero-sub">
            How we collect, use, and protect your personal and business data across the Tecniqa platform.
          </p>

          <div className="pp-hero-meta">
            <span className="pp-meta-badge">
              <ShieldIcon style={{ fontSize: 13 }} />
              Effective: May 2026
            </span>
            <span className="pp-meta-badge">12 Sections</span>
          </div>

        </div>
      </div>

      {/* BODY */}
      <div className="pp-body">

        {/* INTRO CARD */}
        <div className="pp-intro-card">
          <p>
            At <strong>Tecniqa</strong>, your privacy is important to us. This Privacy Policy explains what
            information we collect, how we use it, and the steps we take to keep it safe. It applies to
            all users of our platform — individual buyers, business customers, and procurement professionals.
            By using Tecniqa, you agree to the practices described in this policy.
          </p>
        </div>

        {/* ACCORDION */}
        <div className="pp-sections">
          {sections.map((sec) => {
            const isOpen = active === sec.id;
            return (
              <div
                key={sec.id}
                className={`pp-section${isOpen ? " pp-section--open" : ""}`}
                onClick={() => setActive(isOpen ? null : sec.id)}
              >
                <div className="pp-section-header">
                  <div className="pp-section-left">
                    <span className="pp-section-num">{sec.id}</span>
                    <span className="pp-section-title">{sec.title}</span>
                  </div>
                  <span className="pp-chevron">{isOpen ? "−" : "+"}</span>
                </div>
                {isOpen && (
                  <div className="pp-section-body">
                    <p>{sec.content}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* FOOTER NOTE */}
        <div className="pp-footer-note">
          <span className="pp-footer-label">Privacy concerns or data requests?</span>
          <p>
            Contact our Data Protection Officer at{" "}
            <a href="mailto:support@tecniqa.com">support@tecniqa.com</a> or call{" "}
            <a href="tel:+919668246683">+91 9668 246 683</a> during business hours (Mon–Sat, 9 AM – 6 PM).
          </p>
        </div>

      </div>
    </div>
  );
}